import React from 'react';
import { 
  Box, 
  Chip, 
  Typography 
} from '@mui/material';
import { 
  DataGrid, 
  GridColDef, 
  GridRenderCellParams,
  GridValueFormatterParams
} from '@mui/x-data-grid';
import { PaymentTrackingUISearchResultModel } from '../../services/api/paymentsApi';

interface DashboardTableProps {
  data: PaymentTrackingUISearchResultModel[];
  onRowClick: (payment: PaymentTrackingUISearchResultModel) => void;
}

// Status chip component
const StatusChip = ({ status }: { status: string }) => {
  const getStatusColor = () => {
    switch (status.toLowerCase()) {
      case 'succeeded':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      case 'outbound':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Chip 
      label={status} 
      size="small" 
      color={getStatusColor() as any}
      variant="outlined"
    />
  );
};

// Extended interface for GridValueFormatterParams with row
interface ExtendedGridValueFormatterParams<T> extends GridValueFormatterParams<T> {
  row: any;
}

// Format currency
const formatCurrency = (params: ExtendedGridValueFormatterParams<number>) => {
  // Check if params or value is not provided
  if (!params || params.value === undefined) return '-';
  
  const { value, row } = params;
  
  // Return dash for zero or invalid amounts
  if (value === 0 || value === null) return '-';
  
  // Safety check for row object
  if (!row) return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  
  // Get currency with fallback to GBP
  const currency = row.fromCurrency || 'GBP';
  
  try {
    return new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    // Return a formatted number without currency if there's an error
    console.warn('Error formatting currency:', error);
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
};

// Format date
const formatDate = (params: GridValueFormatterParams<string>) => {
  const value = params.value;
  if (!value) return '-';
  
  try {
    const date = new Date(value);
    return date.toLocaleString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return value;
  }
};

const DashboardTable: React.FC<DashboardTableProps> = ({ data, onRowClick }) => {
  // Define columns
  const columns: GridColDef[] = [
    { 
      field: 'paymentId', 
      headerName: 'Payment ID',
      flex: 1,
      valueGetter: (params) => params.row.externalPaymentId,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'direction', 
      headerName: 'Direction',
      width: 100,
      valueGetter: (params) => params.row.direction || 'Outbound',
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value} 
          size="small" 
          color="primary"
          variant="outlined"
        />
      ) 
    },
    { 
      field: 'startTime', 
      headerName: 'Start Time',
      width: 160,
      valueFormatter: formatDate
    },
    { 
      field: 'endTime', 
      headerName: 'End Time',
      width: 160,
      valueFormatter: formatDate
    },
    { 
      field: 'from', 
      headerName: 'From',
      width: 140,
      valueGetter: (params) => params.row.fromAmount,
      valueFormatter: formatCurrency as any,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Typography variant="body2">
            {params.formattedValue}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'to', 
      headerName: 'To',
      width: 140,
      valueGetter: (params) => params.row.toAmount,
      valueFormatter: ((params: ExtendedGridValueFormatterParams<number>) => {
        // Check if params or value is not provided
        if (!params || params.value === undefined) return '-';
        
        const { value, row } = params;
        
        // Return dash for zero or invalid amounts
        if (value === 0 || value === null) return '-';
        
        // Safety check for row object
        if (!row) return new Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
        
        // Get currency with fallback to GBP
        const currency = row.toCurrency || 'GBP';
        
        try {
          return new Intl.NumberFormat('en-GB', { 
            style: 'currency', 
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value);
        } catch (error) {
          // Return a formatted number without currency if there's an error
          console.warn('Error formatting currency:', error);
          return new Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value);
        }
      }) as any,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Typography variant="body2">
            {params.formattedValue}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'paymentMethod', 
      headerName: 'Method',
      width: 120
    },
    { 
      field: 'paymentStatus', 
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <StatusChip status={params.value} />
      )
    },
    { 
      field: 'actions', 
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip 
            label="View" 
            size="small" 
            color="primary"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              onRowClick(params.row);
            }}
          />
          <Chip 
            label="PDF" 
            size="small" 
            color="secondary"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              // Handle PDF download
              console.log('Download PDF for:', params.row.aggregateId);
            }}
          />
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ height: 400, width: '100%', mt: 2 }}>
      <DataGrid
        rows={data.map(payment => ({
          ...payment,
          id: payment.aggregateId
        }))}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
          sorting: {
            sortModel: [{ field: 'startTime', sort: 'desc' }],
          },
        }}
        pageSizeOptions={[5, 10, 25]}
        onRowClick={(params) => onRowClick(params.row)}
        sx={{
          '& .MuiDataGrid-cell:hover': {
            color: 'primary.main',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(0, 102, 183, 0.04)',
          },
          border: 'none',
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #f0f0f0'
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f8f9fa',
            borderBottom: '2px solid #e0e0e0'
          }
        }}
      />
    </Box>
  );
};

export default DashboardTable; 